/**
 * Diario breve. Cada entrada es un registro propio (varias por día si se quiere):
 * dos dispositivos que escriben el mismo día sin conexión no se pisan.
 */
import { toDayNumber, type LocalDate } from '@habitos/engine';
import { write } from '../db/repo';
import { uuidv7 } from '../lib/ids';
import type { JournalRec } from './model';

/** Preguntas opcionales y neutras. Se puede escribir sin responder ninguna. */
export const JOURNAL_PROMPTS = ['went_well', 'remember', 'differently', 'grateful', 'tomorrow'] as const;
export type PromptKey = (typeof JOURNAL_PROMPTS)[number];

/** Pregunta sugerida del día: rota de forma determinista según la fecha. */
export function suggestedPrompt(date: LocalDate): PromptKey {
  return JOURNAL_PROMPTS[toDayNumber(date) % JOURNAL_PROMPTS.length]!;
}

export function entriesOn(journal: JournalRec[], date: LocalDate): JournalRec[] {
  return journal.filter((j) => !j.deletedAt && j.date === date).sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
}

export interface JournalDraft {
  date: LocalDate;
  text: string;
  prompts: { key: string; answer: string }[];
  includeInReview: boolean;
}

/** Una entrada vacía (sin texto ni respuestas) no se guarda. */
export function isEmptyDraft(d: JournalDraft): boolean {
  return !d.text.trim() && d.prompts.every((p) => !p.answer.trim());
}

/** Crea (id null) o actualiza una entrada. Devuelve su id. */
export async function saveJournal(id: string | null, d: JournalDraft): Promise<string> {
  const prompts = d.prompts.map((p) => ({ key: p.key, answer: p.answer.trim() })).filter((p) => p.answer);
  if (id) {
    await write('journal', id, { text: d.text.trim(), prompts, includeInReview: d.includeInReview });
    return id;
  }
  const newId = uuidv7();
  await write('journal', newId, { date: d.date, text: d.text.trim(), prompts, includeInReview: d.includeInReview, createdAt: new Date().toISOString(), deletedAt: null });
  return newId;
}

export async function deleteJournal(id: string): Promise<void> {
  await write('journal', id, { deletedAt: new Date().toISOString() });
}

export async function restoreJournal(id: string): Promise<void> {
  await write('journal', id, { deletedAt: null });
}
